import React, { useState } from 'react';
import { HiOutlineCheckCircle, HiOutlineExclamationCircle, HiOutlineClipboardCopy } from 'react-icons/hi';
import './SmtpResult.css';

/**
 * Split a stored email_logs.error_message into a readable part and the raw
 * server reply. Stored form: "<title>. <hint> (SMTP: <raw>)"
 */
export const parseSendError = (message) => {
    if (!message) return null;
    const marker = message.indexOf(' (SMTP: ');
    const friendly = marker >= 0 ? message.slice(0, marker) : message;
    const raw = marker >= 0 ? message.slice(marker + 8).replace(/\)$/, '') : null;
    const dot = friendly.indexOf('. ');
    return {
        title: dot >= 0 ? friendly.slice(0, dot) : friendly,
        hint: dot >= 0 ? friendly.slice(dot + 2) : '',
        raw
    };
};

const STAGE_LABELS = {
    connect: 'Connecting to mail server',
    starttls: 'Securing the connection (STARTTLS)',
    login: 'Signing in to the mailbox',
    done: 'Signed in'
};

const TechnicalDetails = ({ lines }) => {
    const [open, setOpen] = useState(false);
    const text = lines.filter(Boolean).join('\n');
    if (!text) return null;

    const copy = () => {
        try { navigator.clipboard.writeText(text); } catch (e) { /* clipboard blocked */ }
    };

    return (
        <div className="smtp-tech">
            <button type="button" className="smtp-tech-toggle" onClick={() => setOpen(!open)}>
                {open ? 'Hide technical details' : 'Show technical details'}
            </button>
            {open && (
                <div className="smtp-tech-body">
                    <pre>{text}</pre>
                    <button type="button" className="smtp-tech-copy" onClick={copy} title="Copy for IT / Microsoft support">
                        <HiOutlineClipboardCopy /> Copy
                    </button>
                </div>
            )}
        </div>
    );
};

/** Result card for a connection test ({ok, stage, title, hint, raw, server, email, elapsed_ms}) */
export const SmtpCheckResult = ({ result }) => {
    if (!result) return null;
    return (
        <div className={`smtp-result ${result.ok ? 'ok' : 'fail'}`}>
            <div className="smtp-result-title">
                {result.ok ? <HiOutlineCheckCircle /> : <HiOutlineExclamationCircle />}
                {result.title}
            </div>
            {result.hint && <p className="smtp-result-hint">{result.hint}</p>}
            <TechnicalDetails lines={[
                result.email && `Mailbox: ${result.email}`,
                result.server && `Server: ${result.server}`,
                result.stage && `Step: ${STAGE_LABELS[result.stage] || result.stage}${result.ok ? '' : ' (failed here)'}`,
                result.elapsed_ms != null && `Time: ${(result.elapsed_ms / 1000).toFixed(1)}s`,
                result.raw && `Server reply: ${result.raw}`,
                `Tested: ${new Date().toLocaleString()}`
            ]} />
        </div>
    );
};

/** Same card for a stored send error string */
export const SendErrorResult = ({ message, prefix }) => {
    const parsed = parseSendError(message);
    if (!parsed) return null;
    return (
        <div className="smtp-result fail">
            <div className="smtp-result-title">
                <HiOutlineExclamationCircle />
                {prefix}{parsed.title}
            </div>
            {parsed.hint && <p className="smtp-result-hint">{parsed.hint}</p>}
            {parsed.raw && <TechnicalDetails lines={[`Server reply: ${parsed.raw}`]} />}
        </div>
    );
};
